import { AttributeData, OperationMetadata, SupportedOperation, SnowAttribute, DataType } from '@amzn/snow-data-service-typescript-client';
import { ExportOperationAsTaskRequest } from '@amzn/snow-nxt-workflow-service-typescript-client';
import { CombinedOperation, IntegrationType } from 'src/@types/serviceOnboardingTypes/operation.interface';
import { UIDataType, UISnowAttribute } from 'src/@types/serviceOnboardingTypes/attribute.types';
import { WizardStep } from 'src/components/commonComponents/GenericWizard/GenericWizard';
import { Override } from 'src/@types/common';



export type ExportTaskFormData = Override<ExportOperationAsTaskRequest, {integrationType: IntegrationType}> 

export const EXPORT_TASK_FORM_ATTRIBUTES: UISnowAttribute[] = [
	{
		attributeId: 'taskName',
		attributeName: 'Task Name',
		description: 'Enter a unique name for the task',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText: 'Task name must be at least 5 characters',
		regex: '^.{5,100}$',
	},
	{
		attributeId: 'operationName',
		attributeName: 'Operation Name',
		description: 'Name of the operation',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText: 'Operation name must be at least 3 characters',
		regex: '^.{3,}$',
	},
	{
		attributeId: 'workflowId',
		attributeName: 'Workflow ID',
		description: 'Enter the workflow ID',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText:
			'Workflow ID can only contain letters, numbers, hyphens, and underscores',
		regex: '^[A-Za-z0-9-_]+$',
	},
	{
		attributeId: 'description',
		attributeName: 'Description of the Task',
		description: 'Provide a detailed description of the task',
		dataType: UIDataType.PARAGRAPH,
		mandatory: true,
		helpText: 'Description must be at least 10 characters',
		regex: '^.{10,}$',
	},
	{
		attributeId: 'owner',
		attributeName: 'Owner',
		description: 'Enter the owner of the task',
		dataType: UIDataType.STRING,
		mandatory: true,
		helpText: 'Enter the task owner',
		regex: '^.+$',
	},
	{
		attributeId: 'rank',
		attributeName: 'Rank',
		description: 'Enter the rank for the task',
		dataType: UIDataType.NUMBER,
		mandatory: true,
		helpText: 'Rank must be at least 1',
		regex: '^[1-9]\\d*$',
	},
	{
		attributeId: 'hopRank',
		attributeName: 'Hop Rank',
		description: 'Enter the hop rank for the task',
		dataType: UIDataType.NUMBER,
		mandatory: true,
		helpText: 'Hop Rank must be at least 1',
		regex: '^[1-9]\\d*$',
	},
	{
		attributeId: 'integrationType',
		attributeName: 'Integration Type',
		description: 'Type of integration',
		dataType: UIDataType.STRING,
		mandatory: true,
		options: [IntegrationType.APIGATEWAY, IntegrationType.CORAL],
	},
];

export function mergeSupportedOperationAndMetadata(
	supportedOperation?: SupportedOperation,
	metadata?: OperationMetadata
): CombinedOperation {
	return {
		...supportedOperation,
		...metadata,
	};
}

/**
 * Maps AttributeData from the service to SnowAttribute for form rendering.
 */
export function mapAttributeDataToSnowAttribute(attr: AttributeData): SnowAttribute {
	return {
		attributeId: attr.attributeId ?? attr.attributeName,
		attributeName: attr.attributeName,
		description: attr.description,
		dataType: (attr.dataType as DataType) ?? DataType.STRING,
		regex: attr.regex,
		helpText: attr.helpText,
		options: attr.options,
		mandatory: attr.mandatory,
		value: attr.value,
		duplicate: attr.duplicate,
		attributeType: attr.attributeType,
		caseHandlingType: attr.caseHandlingType,
		renderingType: attr.renderingType,
	};
}

export const DEFAULT_EXPORT_TASK_DATA: ExportTaskFormData = {
	operationId: '',
	taskName: '',
	operationName: '',
	workflowId: '',
	description: '',
	owner: '',
	rank: 1,
	hopRank: 1,
	integrationType: IntegrationType.CORAL,
	taskInput: [],
	taskOutput: [],
	user:'',
};

export function getExportTaskWizardSteps(): WizardStep<ExportTaskFormData>[] {
	return [
		{
			title: 'Task Configuration',
			description: 'Configure the task details and parameters',
			attributes: EXPORT_TASK_FORM_ATTRIBUTES,
			reviewGroup: 'Task Configuration',
		},
	];
}
